/**
 * Stella Country Phone Validation & Metadata Utility
 * Standardized across all customer registration and profile update workflows.
 * Enforces country-specific dialing codes, length bounds, and format regex patterns.
 */

export const DEFAULT_PHONE_DATA = {
  dialCode: '+',
  minLength: 7,
  maxLength: 15,
  pattern: '^\\d{7,15}$',
  regex: /^\d{7,15}$/,
  placeholder: 'Phone number',
  hint: '7-15 digits (ITU-T E.164)',
  countryName: 'International'
};

export const COUNTRY_PHONE_DATA = {
  US: {
    dialCode: '+1',
    minLength: 10,
    maxLength: 10,
    pattern: '^[2-9]\\d{9}$',
    regex: /^[2-9]\d{9}$/,
    placeholder: '555 123 4567',
    example: '5551234567',
    hint: '10 digits (e.g. 555 123 4567)',
    countryName: 'United States'
  },
  CA: {
    dialCode: '+1',
    minLength: 10,
    maxLength: 10,
    pattern: '^[2-9]\\d{9}$',
    regex: /^[2-9]\d{9}$/,
    placeholder: '555 123 4567',
    example: '5551234567',
    hint: '10 digits (e.g. 555 123 4567)',
    countryName: 'Canada'
  },
  IN: {
    dialCode: '+91',
    minLength: 10,
    maxLength: 10,
    pattern: '^[6-9]\\d{9}$',
    regex: /^[6-9]\d{9}$/,
    placeholder: '98765 43210',
    example: '9876543210',
    hint: '10 digits starting with 6-9',
    countryName: 'India'
  },
  GB: {
    dialCode: '+44',
    minLength: 10,
    maxLength: 11,
    pattern: '^(7\\d{9}|[1-9]\\d{8,10})$',
    regex: /^(7\d{9}|[1-9]\d{8,10})$/,
    placeholder: '7123 456789',
    example: '7123456789',
    hint: '10-11 digits (e.g. 7123 456789)',
    countryName: 'United Kingdom'
  },
  AU: {
    dialCode: '+61',
    minLength: 9,
    maxLength: 10,
    pattern: '^(4\\d{8}|[2378]\\d{8})$',
    regex: /^(4\d{8}|[2378]\d{8})$/,
    placeholder: '412 345 678',
    example: '412345678',
    hint: '9-10 digits (e.g. 412 345 678)',
    countryName: 'Australia'
  },
  DE: {
    dialCode: '+49',
    minLength: 10,
    maxLength: 11,
    pattern: '^(1[5-7]\\d{8,9}|[2-9]\\d{8,10})$',
    regex: /^(1[5-7]\d{8,9}|[2-9]\d{8,10})$/,
    placeholder: '151 23456789',
    example: '15123456789',
    hint: '10-11 digits',
    countryName: 'Germany'
  },
  FR: {
    dialCode: '+33',
    minLength: 9,
    maxLength: 9,
    pattern: '^[67]\\d{8}$|^[1-59]\\d{8}$',
    regex: /^([67]\d{8}|[1-59]\d{8})$/,
    placeholder: '6 12 34 56 78',
    example: '612345678',
    hint: '9 digits (e.g. 6 12 34 56 78)',
    countryName: 'France'
  },
  AE: {
    dialCode: '+971',
    minLength: 9,
    maxLength: 9,
    pattern: '^5\\d{8}$',
    regex: /^5\d{8}$/,
    placeholder: '50 123 4567',
    example: '501234567',
    hint: '9 digits starting with 5',
    countryName: 'United Arab Emirates'
  },
  SA: {
    dialCode: '+966',
    minLength: 9,
    maxLength: 9,
    pattern: '^5\\d{8}$',
    regex: /^5\d{8}$/,
    placeholder: '50 123 4567',
    example: '501234567',
    hint: '9 digits starting with 5',
    countryName: 'Saudi Arabia'
  },
  SG: {
    dialCode: '+65',
    minLength: 8,
    maxLength: 8,
    pattern: '^[89]\\d{7}$',
    regex: /^[89]\d{7}$/,
    placeholder: '8123 4567',
    example: '81234567',
    hint: '8 digits starting with 8 or 9',
    countryName: 'Singapore'
  },
  JP: {
    dialCode: '+81',
    minLength: 10,
    maxLength: 10,
    pattern: '^([789]0\\d{8}|[1-9]\\d{9})$',
    regex: /^([789]0\d{8}|[1-9]\d{9})$/,
    placeholder: '90 1234 5678',
    example: '9012345678',
    hint: '10 digits',
    countryName: 'Japan'
  },
  CN: {
    dialCode: '+86',
    minLength: 11,
    maxLength: 11,
    pattern: '^1[3-9]\\d{9}$',
    regex: /^1[3-9]\d{9}$/,
    placeholder: '138 0013 8000',
    example: '13800138000',
    hint: '11 digits starting with 1',
    countryName: 'China'
  },
  BR: {
    dialCode: '+55',
    minLength: 10,
    maxLength: 11,
    pattern: '^[1-9]{2}9?\\d{8}$',
    regex: /^[1-9]{2}9?\d{8}$/,
    placeholder: '11 98765 4321',
    example: '11987654321',
    hint: '10-11 digits (with DDD area code)',
    countryName: 'Brazil'
  },
  MX: {
    dialCode: '+52',
    minLength: 10,
    maxLength: 10,
    pattern: '^[1-9]\\d{9}$',
    regex: /^[1-9]\d{9}$/,
    placeholder: '55 1234 5678',
    example: '5512345678',
    hint: '10 digits',
    countryName: 'Mexico'
  },
  IT: {
    dialCode: '+39',
    minLength: 9,
    maxLength: 11,
    pattern: '^(3\\d{8,9}|[0-9]\\d{8,10})$',
    regex: /^(3\d{8,9}|[0-9]\d{8,10})$/,
    placeholder: '312 345 6789',
    example: '3123456789',
    hint: '9-11 digits',
    countryName: 'Italy'
  },
  ES: {
    dialCode: '+34',
    minLength: 9,
    maxLength: 9,
    pattern: '^[6789]\\d{8}$',
    regex: /^[6789]\d{8}$/,
    placeholder: '612 345 678',
    example: '612345678',
    hint: '9 digits starting with 6, 7, 8, or 9',
    countryName: 'Spain'
  },
  NL: {
    dialCode: '+31',
    minLength: 9,
    maxLength: 9,
    pattern: '^(6\\d{8}|[1-9]\\d{8})$',
    regex: /^(6\d{8}|[1-9]\d{8})$/,
    placeholder: '6 12345678',
    example: '612345678',
    hint: '9 digits',
    countryName: 'Netherlands'
  },
  RU: {
    dialCode: '+7',
    minLength: 10,
    maxLength: 10,
    pattern: '^[9348]\\d{9}$',
    regex: /^[9348]\d{9}$/,
    placeholder: '912 345 6789',
    example: '9123456789',
    hint: '10 digits',
    countryName: 'Russia'
  },
  ZA: {
    dialCode: '+27',
    minLength: 9,
    maxLength: 9,
    pattern: '^[6-8]\\d{8}$',
    regex: /^[6-8]\d{8}$/,
    placeholder: '71 234 5678',
    example: '712345678',
    hint: '9 digits starting with 6-8',
    countryName: 'South Africa'
  },
  NG: {
    dialCode: '+234',
    minLength: 10,
    maxLength: 10,
    pattern: '^[789]\\d{9}$',
    regex: /^[789]\d{9}$/,
    placeholder: '802 123 4567',
    example: '8021234567',
    hint: '10 digits',
    countryName: 'Nigeria'
  },
  PH: {
    dialCode: '+63',
    minLength: 10,
    maxLength: 10,
    pattern: '^9\\d{9}$',
    regex: /^9\d{9}$/,
    placeholder: '917 123 4567',
    example: '9171234567',
    hint: '10 digits starting with 9',
    countryName: 'Philippines'
  },
  PK: {
    dialCode: '+92',
    minLength: 10,
    maxLength: 10,
    pattern: '^3\\d{9}$',
    regex: /^3\d{9}$/,
    placeholder: '300 1234567',
    example: '3001234567',
    hint: '10 digits starting with 3',
    countryName: 'Pakistan'
  },
  BD: {
    dialCode: '+880',
    minLength: 10,
    maxLength: 10,
    pattern: '^1[3-9]\\d{8}$',
    regex: /^1[3-9]\d{8}$/,
    placeholder: '1712 345678',
    example: '1712345678',
    hint: '10 digits starting with 1',
    countryName: 'Bangladesh'
  },
  ID: {
    dialCode: '+62',
    minLength: 9,
    maxLength: 12,
    pattern: '^8\\d{8,11}$',
    regex: /^8\d{8,11}$/,
    placeholder: '812 3456 7890',
    example: '81234567890',
    hint: '9-12 digits starting with 8',
    countryName: 'Indonesia'
  },
  NZ: {
    dialCode: '+64',
    minLength: 8,
    maxLength: 10,
    pattern: '^(2\\d{7,9}|[3-9]\\d{7})$',
    regex: /^(2\d{7,9}|[3-9]\d{7})$/,
    placeholder: '21 123 4567',
    example: '211234567',
    hint: '8-10 digits',
    countryName: 'New Zealand'
  },
  IE: {
    dialCode: '+353',
    minLength: 9,
    maxLength: 9,
    pattern: '^(8[3-9]\\d{7}|[1-9]\\d{8})$',
    regex: /^(8[3-9]\d{7}|[1-9]\d{8})$/,
    placeholder: '85 123 4567',
    example: '851234567',
    hint: '9 digits',
    countryName: 'Ireland'
  },
  CH: {
    dialCode: '+41',
    minLength: 9,
    maxLength: 9,
    pattern: '^(7[5-9]\\d{7}|[2-9]\\d{8})$',
    regex: /^(7[5-9]\d{7}|[2-9]\d{8})$/,
    placeholder: '79 123 45 67',
    example: '791234567',
    hint: '9 digits',
    countryName: 'Switzerland'
  },
  SE: {
    dialCode: '+46',
    minLength: 9,
    maxLength: 10,
    pattern: '^(7\\d{8}|[1-9]\\d{8,9})$',
    regex: /^(7\d{8}|[1-9]\d{8,9})$/,
    placeholder: '70 123 45 67',
    example: '701234567',
    hint: '9-10 digits',
    countryName: 'Sweden'
  },
  NO: {
    dialCode: '+47',
    minLength: 8,
    maxLength: 8,
    pattern: '^[49]\\d{7}$',
    regex: /^[49]\d{7}$/,
    placeholder: '412 34 567',
    example: '41234567',
    hint: '8 digits starting with 4 or 9',
    countryName: 'Norway'
  },
  DK: {
    dialCode: '+45',
    minLength: 8,
    maxLength: 8,
    pattern: '^[2-9]\\d{7}$',
    regex: /^[2-9]\d{7}$/,
    placeholder: '20 12 34 56',
    example: '20123456',
    hint: '8 digits',
    countryName: 'Denmark'
  },
  FI: {
    dialCode: '+358',
    minLength: 9,
    maxLength: 10,
    pattern: '^[45]\\d{8,9}$',
    regex: /^[45]\d{8,9}$/,
    placeholder: '40 1234567',
    example: '401234567',
    hint: '9-10 digits',
    countryName: 'Finland'
  },
  BE: {
    dialCode: '+32',
    minLength: 9,
    maxLength: 9,
    pattern: '^(4\\d{8}|[1-9]\\d{7,8})$',
    regex: /^(4\d{8}|[1-9]\d{7,8})$/,
    placeholder: '470 12 34 56',
    example: '470123456',
    hint: '9 digits',
    countryName: 'Belgium'
  },
  AT: {
    dialCode: '+43',
    minLength: 10,
    maxLength: 11,
    pattern: '^(6\\d{9,10}|[1-9]\\d{8,10})$',
    regex: /^(6\d{9,10}|[1-9]\d{8,10})$/,
    placeholder: '664 1234567',
    example: '6641234567',
    hint: '10-11 digits',
    countryName: 'Austria'
  },
  PL: {
    dialCode: '+48',
    minLength: 9,
    maxLength: 9,
    pattern: '^[4-9]\\d{8}$',
    regex: /^[4-9]\d{8}$/,
    placeholder: '512 345 678',
    example: '512345678',
    hint: '9 digits',
    countryName: 'Poland'
  },
  TR: {
    dialCode: '+90',
    minLength: 10,
    maxLength: 10,
    pattern: '^5\\d{9}$',
    regex: /^5\d{9}$/,
    placeholder: '532 123 4567',
    example: '5321234567',
    hint: '10 digits starting with 5',
    countryName: 'Turkey'
  },
  KR: {
    dialCode: '+82',
    minLength: 9,
    maxLength: 10,
    pattern: '^(10\\d{7,8}|[1-9]\\d{7,8})$',
    regex: /^(10\d{7,8}|[1-9]\d{7,8})$/,
    placeholder: '10 1234 5678',
    example: '1012345678',
    hint: '9-10 digits',
    countryName: 'South Korea'
  },
  EG: {
    dialCode: '+20',
    minLength: 10,
    maxLength: 10,
    pattern: '^1[0-2,5]\\d{8}$',
    regex: /^1[0-2,5]\d{8}$/,
    placeholder: '100 123 4567',
    example: '1001234567',
    hint: '10 digits starting with 1',
    countryName: 'Egypt'
  },
  MY: {
    dialCode: '+60',
    minLength: 9,
    maxLength: 10,
    pattern: '^1\\d{8,9}$',
    regex: /^1\d{8,9}$/,
    placeholder: '12 345 6789',
    example: '123456789',
    hint: '9-10 digits starting with 1',
    countryName: 'Malaysia'
  },
  TH: {
    dialCode: '+66',
    minLength: 9,
    maxLength: 9,
    pattern: '^[689]\\d{8}$',
    regex: /^[689]\d{8}$/,
    placeholder: '81 234 5678',
    example: '812345678',
    hint: '9 digits starting with 6, 8, or 9',
    countryName: 'Thailand'
  },
  VN: {
    dialCode: '+84',
    minLength: 9,
    maxLength: 10,
    pattern: '^[35789]\\d{8}$',
    regex: /^[35789]\d{8}$/,
    placeholder: '91 234 5678',
    example: '912345678',
    hint: '9-10 digits',
    countryName: 'Vietnam'
  },
  AR: {
    dialCode: '+54',
    minLength: 10,
    maxLength: 10,
    pattern: '^(9?\\d{10})$',
    regex: /^(9?\d{10})$/,
    placeholder: '11 2345 6789',
    example: '1123456789',
    hint: '10 digits',
    countryName: 'Argentina'
  },
  CO: {
    dialCode: '+57',
    minLength: 10,
    maxLength: 10,
    pattern: '^3\\d{9}$',
    regex: /^3\d{9}$/,
    placeholder: '300 123 4567',
    example: '3001234567',
    hint: '10 digits starting with 3',
    countryName: 'Colombia'
  },
  CL: {
    dialCode: '+56',
    minLength: 9,
    maxLength: 9,
    pattern: '^9\\d{8}$',
    regex: /^9\d{8}$/,
    placeholder: '9 1234 5678',
    example: '912345678',
    hint: '9 digits starting with 9',
    countryName: 'Chile'
  },
  PE: {
    dialCode: '+51',
    minLength: 9,
    maxLength: 9,
    pattern: '^9\\d{8}$',
    regex: /^9\d{8}$/,
    placeholder: '912 345 678',
    example: '912345678',
    hint: '9 digits starting with 9',
    countryName: 'Peru'
  },
  IL: {
    dialCode: '+972',
    minLength: 9,
    maxLength: 9,
    pattern: '^5\\d{8}$',
    regex: /^5\d{8}$/,
    placeholder: '50 123 4567',
    example: '501234567',
    hint: '9 digits starting with 5',
    countryName: 'Israel'
  },
  PT: {
    dialCode: '+351',
    minLength: 9,
    maxLength: 9,
    pattern: '^(9\\d{8}|[23]\\d{8})$',
    regex: /^(9\d{8}|[23]\d{8})$/,
    placeholder: '912 345 678',
    example: '912345678',
    hint: '9 digits starting with 9',
    countryName: 'Portugal'
  },
  GR: {
    dialCode: '+30',
    minLength: 10,
    maxLength: 10,
    pattern: '^(69\\d{8}|2\\d{9})$',
    regex: /^(69\d{8}|2\d{9})$/,
    placeholder: '69 1234 5678',
    example: '6912345678',
    hint: '10 digits',
    countryName: 'Greece'
  },
  HK: {
    dialCode: '+852',
    minLength: 8,
    maxLength: 8,
    pattern: '^[569]\\d{7}$',
    regex: /^[569]\d{7}$/,
    placeholder: '9123 4567',
    example: '91234567',
    hint: '8 digits',
    countryName: 'Hong Kong'
  },
  TW: {
    dialCode: '+886',
    minLength: 9,
    maxLength: 9,
    pattern: '^9\\d{8}$',
    regex: /^9\d{8}$/,
    placeholder: '912 345 678',
    example: '912345678',
    hint: '9 digits starting with 9',
    countryName: 'Taiwan'
  },
  KE: {
    dialCode: '+254',
    minLength: 9,
    maxLength: 9,
    pattern: '^[17]\\d{8}$',
    regex: /^[17]\d{8}$/,
    placeholder: '712 345 678',
    example: '712345678',
    hint: '9 digits',
    countryName: 'Kenya'
  },
  GH: {
    dialCode: '+233',
    minLength: 9,
    maxLength: 9,
    pattern: '^[235]\\d{8}$',
    regex: /^[235]\d{8}$/,
    placeholder: '24 123 4567',
    example: '241234567',
    hint: '9 digits',
    countryName: 'Ghana'
  }
};

/**
 * Returns phone metadata for a given 2-letter ISO country code.
 * Falls back to DEFAULT_PHONE_DATA if country is not explicitly cataloged.
 *
 * @param {string} countryCode
 * @returns {object}
 */
export function getPhoneMetadata(countryCode) {
  if (!countryCode || typeof countryCode !== 'string') {
    return { ...DEFAULT_PHONE_DATA };
  }
  const code = countryCode.trim().toUpperCase();
  const data = COUNTRY_PHONE_DATA[code];
  if (data) {
    return { ...data, countryCode: code };
  }
  return { ...DEFAULT_PHONE_DATA, countryCode: code };
}

/**
 * Cleans and sanitizes raw phone input:
 * 1. Strips all non-digit characters.
 * 2. If the user pasted a number prefixed with the country's dial code (e.g. 919876543210 for IN),
 *    strips the redundant dial code prefix when length exceeds national max.
 * 3. If the user pasted a national trunk zero prefix (e.g. 07123456789 for GB), strips the leading '0'.
 * 4. Strictly clamps the maximum length to meta.maxLength.
 *
 * @param {string|number} rawNumber
 * @param {string} countryCode
 * @returns {string} Clean digits strictly within [0, meta.maxLength]
 */
export function cleanPhoneNumber(rawNumber, countryCode) {
  if (rawNumber === null || rawNumber === undefined) {
    return '';
  }
  let str = String(rawNumber).replace(/[^\d]/g, '');
  if (!str) {
    return '';
  }

  const meta = getPhoneMetadata(countryCode);
  const dialDigits = meta.dialCode.replace(/[^\d]/g, '');

  // Strip country dial code if user pasted it and total length exceeds maxLength
  if (dialDigits && str.startsWith(dialDigits) && str.length > meta.maxLength) {
    str = str.slice(dialDigits.length);
  }

  // Strip leading national trunk zero if present (e.g., 07123456789 for GB, 0612345678 for FR)
  if (str.startsWith('0') && str.length >= meta.minLength + 1) {
    str = str.slice(1);
  }

  // Truncate to maximum allowed length for the country
  if (str.length > meta.maxLength) {
    str = str.slice(0, meta.maxLength);
  }

  return str;
}

/**
 * Validates a phone number against country-specific rules.
 *
 * @param {string|number} rawNumber
 * @param {string} countryCode
 * @returns {{ isValid: boolean, message: string|null, cleaned: string, meta: object }}
 */
export function validatePhoneNumber(rawNumber, countryCode) {
  const meta = getPhoneMetadata(countryCode);
  const countryName = meta.countryName || countryCode || 'Selected Country';

  if (rawNumber === null || rawNumber === undefined || String(rawNumber).trim() === '') {
    return {
      isValid: false,
      message: 'Phone number is required',
      cleaned: '',
      meta
    };
  }

  const cleaned = cleanPhoneNumber(rawNumber, countryCode);

  if (!cleaned) {
    return {
      isValid: false,
      message: 'Phone number must contain digits only',
      cleaned: '',
      meta
    };
  }

  if (cleaned.length < meta.minLength) {
    const plural = meta.minLength === 1 ? 'digit' : 'digits';
    return {
      isValid: false,
      message: `${countryName} phone number must be at least ${meta.minLength} ${plural}`,
      cleaned,
      meta
    };
  }

  if (cleaned.length > meta.maxLength) {
    return {
      isValid: false,
      message: `${countryName} phone number cannot exceed ${meta.maxLength} digits`,
      cleaned,
      meta
    };
  }

  if (meta.regex && !meta.regex.test(cleaned)) {
    return {
      isValid: false,
      message: `Invalid format for ${countryName} (${meta.hint})`,
      cleaned,
      meta
    };
  }

  return {
    isValid: true,
    message: null,
    cleaned,
    meta
  };
}

/**
 * Boolean helper for Vuelidate custom validators.
 *
 * @param {string|number} phone
 * @param {string} countryCode
 * @returns {boolean}
 */
export function isPhoneValidForCountry(phone, countryCode) {
  return validatePhoneNumber(phone, countryCode).isValid;
}
